/**
 * 官网单点登录：账号状态与「我的应用」数据（设计文档 doc/account/官网单点登录-设计方案.md §5.2 / §5.3）。
 *
 * 【浏览器零令牌】登录态完全由官网后端（BFF）用 HttpOnly 会话 Cookie `__Host-hp_web` 维护，
 * 本模块不读、不写、不缓存任何令牌。
 *
 * 【展示数据短缓存】为了首屏顶栏与「我的应用」秒出，/api/v1/me 的展示资料（uid、昵称、脱敏邮箱、头像地址）
 * 与 /me/apps 第一页放进 sessionStorage（仅当前标签页，TTL 60s，不进 localStorage），先用缓存渲染再后台
 * 刷新（stale-while-revalidate）。缓存只是展示加速，不代表登录态：后台刷新拿到 401 / 404 立即清掉并切换状态；
 * 应用列表缓存带 uid，与当前账号不一致即作废；退出、切换账号、会话失效时一并清除。
 * 所有请求显式 `credentials: 'same-origin'`：与官网同源的 /api/v1 才带 Cookie，绝不跨站携带。
 *
 * 【功能开关】后端 `OfficialLogin.Enabled` 关闭时 /api/v1/me 返回 404（与现有 Promotion 开关一致）。
 * 前端只把「HTTP 401」当作「功能已开启、但未登录」的正面信号；404 与「功能未开启」业务码视为
 * 「入口不可用」，顶栏不显示登录入口 —— 后端没上线或开关没开时页面零报错。
 *
 * 【暂时性故障自动重试】/api/v1/me 与 /api/v1/me/apps 遇到 502 / 503 / 504、网络错误、超时时，
 * 按 RetryDelaysMs 退避后自动重试（最多 3 次），重试期间状态保持 loading（骨架屏），不闪错误页；
 * 401 等明确结果不重试。重试后仍失败才落到 error：顶栏照常给「登录」入口，「我的应用」页给可重试的错误卡。
 */
import { ref } from 'vue'
import { buildPublicApiUrl } from './official-promotion'

/** /api/v1/me 返回的账号资料（邮箱已由后端脱敏）。 */
export interface AccountUser {
  uid: string
  name: string
  email: string
  avatar: string
}

/**
 * 账号状态：
 *   · loading   —— 首次请求还没回来；
 *   · disabled  —— 后端开关关闭 / 接口不存在（404 或功能未开启的业务码），不显示任何登录入口；
 *   · anonymous —— HTTP 401，功能已开启但未登录；
 *   · signed-in —— 已登录；
 *   · error     —— 网络错误、超时、5xx（暂时性故障已自动重试仍失败）：状态未知，顶栏仍显示「登录」入口
 *                  （登录后回到当前页），「我的应用」页给出可重试 / 重新登录的错误态。
 */
export type AccountState =
  | { kind: 'loading' }
  | { kind: 'disabled' }
  | { kind: 'anonymous' }
  | { kind: 'signed-in'; user: AccountUser }
  | { kind: 'error' }

/** 「我的应用」列表里的一项（只保留白名单字段）。 */
export interface InstalledApp {
  appName: string
  icon: string
  versionName: string
  /** 秒级时间戳；后端没给或不合法时为 0。 */
  installTime: number
  updateTime: number
}

export type InstalledAppsResult =
  | { kind: 'ok'; list: InstalledApp[]; total: number; page: number; pageSize: number }
  | { kind: 'unauthorized' }
  | { kind: 'error' }

/** 登录完成后回到的站内页面。只给站内相对路径，后端还会再校验一次（§6 开放重定向）。 */
export const MyAppsPath = '/me'

/** 「我的应用」每页条数（后端上限 50）。 */
export const InstalledAppsPageSize = 20

/** 单次请求的超时时间：超时按 error 处理，顶栏不会一直卡在加载态。 */
const RequestTimeoutMs = 8000

/** 全站共享的账号状态（顶栏入口与「我的应用」页共用同一次请求结果）。 */
export const accountState = ref<AccountState>({ kind: 'loading' })

let inflight: Promise<AccountState> | null = null

/* ── sessionStorage 短缓存（老内核 / 隐私模式下读写会抛错，一律静默降级为不缓存）──────── */

/** 缓存有效期：60 秒。 */
export const AccountCacheTtlMs = 60000
const AccountCacheKey = 'hp_official_acct_v1'
const AppsCacheKey = 'hp_official_apps_v1'

function readCache(key: string): unknown {
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { at?: unknown; data?: unknown }
    const age = typeof parsed.at === 'number' ? Date.now() - parsed.at : -1
    if (age < 0 || age > AccountCacheTtlMs) {
      window.sessionStorage.removeItem(key)
      return null
    }
    return parsed.data
  } catch {
    return null
  }
}

function writeCache(key: string, data: unknown): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), data }))
  } catch {
    // 配额满 / 禁用存储：不缓存即可
  }
}

/** 清掉账号与应用列表的短缓存（退出、切换账号、会话失效时调用）。 */
export function clearAccountCache(): void {
  try {
    window.sessionStorage.removeItem(AccountCacheKey)
    window.sessionStorage.removeItem(AppsCacheKey)
  } catch {
    // 忽略
  }
}

interface ApiEnvelope<T> {
  code?: unknown
  msg?: unknown
  data?: T
}

/** 统一的同源请求：显式 same-origin、带超时，任何异常都转成 null 交给调用方按失败处理。 */
async function requestJson<T>(path: string, init: RequestInit = {}): Promise<{ status: number; body: ApiEnvelope<T> | null } | null> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const extra = init.headers as Record<string, string> | undefined
  if (extra) {
    for (const key of Object.keys(extra)) headers[key] = extra[key]
  }
  const request = fetch(buildPublicApiUrl(path), {
    method: init.method || 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
    headers,
  }).then(async response => {
    let body: ApiEnvelope<T> | null = null
    try {
      body = (await response.json()) as ApiEnvelope<T>
    } catch {
      body = null
    }
    return { status: response.status, body }
  })
  const timeout = new Promise<null>(resolve => window.setTimeout(() => resolve(null), RequestTimeoutMs))
  try {
    return await Promise.race([request, timeout])
  } catch {
    return null
  }
}

/** 暂时性故障的重试退避（毫秒）：首次失败后依次等 0.6s、1.5s、3s 再试，最多重试 3 次。 */
export const RetryDelaysMs: readonly number[] = [600, 1500, 3000]

/** 只有网关类 5xx（502/503/504）、网络错误与超时才算暂时性故障；401、404、500 等明确结果不重试。 */
function isTransientFailure(result: { status: number } | null): boolean {
  if (!result) return true
  return result.status === 0 || result.status === 502 || result.status === 503 || result.status === 504
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms))
}

/** 只读 GET 接口用：暂时性故障按 RetryDelaysMs 退避重试，返回最后一次的结果。 */
async function requestJsonWithRetry<T>(path: string): Promise<{ status: number; body: ApiEnvelope<T> | null } | null> {
  let result = await requestJson<T>(path)
  for (let i = 0; i < RetryDelaysMs.length && isTransientFailure(result); i++) {
    await wait(RetryDelaysMs[i])
    result = await requestJson<T>(path)
  }
  return result
}

function asText(value: unknown, max = 200): string {
  if (typeof value === 'number' && isFinite(value)) return String(value)
  if (typeof value !== 'string') return ''
  return value.trim().slice(0, max)
}

/**
 * 只接受可以安全放进 <img src> 的地址：https / http 绝对地址、站内绝对路径、data:image。
 * 其余（javascript:、协议相对的 //host 等）一律丢弃，交给占位图。
 */
export function safeImageUrl(value: unknown): string {
  const url = asText(value, 2048)
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  if (/^data:image\//i.test(url)) return url
  if (url.charAt(0) === '/' && url.charAt(1) !== '/') return url
  return ''
}

function asTimestamp(value: unknown): number {
  const n = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value
  if (typeof n !== 'number' || !isFinite(n) || n <= 0) return 0
  // 兼容后端误传毫秒：秒级时间戳在 2286 年之前都小于 1e10
  return n > 1e11 ? Math.floor(n / 1000) : Math.floor(n)
}

function toAccountUser(data: unknown): AccountUser | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as Record<string, unknown>
  const uid = asText(raw.uid, 64)
  if (!uid) return null
  return {
    uid,
    name: asText(raw.name, 64),
    email: asText(raw.email, 128),
    avatar: safeImageUrl(raw.avatar),
  }
}

/** 把 /api/v1/me 的响应映射成账号状态（导出供用例与「我的应用」页复用）。 */
export function interpretAccountResponse(result: { status: number; body: ApiEnvelope<unknown> | null } | null): AccountState {
  if (!result) return { kind: 'error' }
  if (result.status === 401) return { kind: 'anonymous' }
  if (result.status === 404) return { kind: 'disabled' }
  if (result.status >= 500 || result.status === 0) return { kind: 'error' }
  const body = result.body
  if (result.status === 200 && body && body.code === 200) {
    const user = toAccountUser(body.data)
    return user ? { kind: 'signed-in', user } : { kind: 'error' }
  }
  // 200 外壳里的非 200 业务码（功能未开启等）、403、其他 4xx：按「入口不可用」处理，不报错。
  return { kind: 'disabled' }
}

/**
 * 读取账号状态。同一时刻只发一个请求；force=true 时重新拉取（重试、退出后刷新）。
 */
export function loadAccount(force = false): Promise<AccountState> {
  // 顶栏与「我的应用」页共用这一个请求：同一页面内只发一次 /api/v1/me
  if (inflight && !force) return inflight
  // 首屏：60s 内的缓存先拿来渲染（stale-while-revalidate），下面照常后台刷新
  const cached = force ? null : toAccountUser(readCache(AccountCacheKey))
  if (force) accountState.value = { kind: 'loading' }
  else if (cached && accountState.value.kind === 'loading') accountState.value = { kind: 'signed-in', user: cached }
  // 暂时性故障在这里自动重试；整个过程 accountState 保持 loading（或缓存态），只在拿到最终结果后更新一次
  const current = requestJsonWithRetry<unknown>('/me').then(result => {
    let next = interpretAccountResponse(result)
    // 后台刷新遇到暂时性故障（重试后仍失败）：沿用缓存的展示，不把已渲染的顶栏打回「未知」
    if (next.kind === 'error' && cached) next = { kind: 'signed-in', user: cached }
    if (next.kind === 'signed-in') {
      if (!cached || cached.uid !== next.user.uid) clearAccountCache()
      writeCache(AccountCacheKey, next.user)
    } else if (next.kind !== 'error') {
      clearAccountCache()
    }
    if (inflight === current) accountState.value = next
    return next
  })
  inflight = current
  return current
}

/** 会话在使用中失效（列表接口返回 401）时同步顶栏，避免顶栏仍显示已登录。 */
export function markSignedOut(): void {
  clearAccountCache()
  accountState.value = { kind: 'anonymous' }
}

/**
 * 登录入口地址：浏览器整页跳转到 BFF，由它 302 到统一登录页（不是 fetch）。
 * prompt=login 时 BFF 透传给 SSO，强制重新输入凭据（「切换账号」用，不会被已有 SSO 会话静默登回原账号）。
 */
export function buildLoginUrl(returnTo: string = MyAppsPath, options: { prompt?: 'login' } = {}): string {
  // 只允许以单个 / 开头的站内路径，与后端 returnTo 校验口径一致
  const target = returnTo.charAt(0) === '/' && returnTo.charAt(1) !== '/' && returnTo.charAt(1) !== '\\' ? returnTo : MyAppsPath
  const prompt = options.prompt === 'login' ? '&prompt=login' : ''
  return `${buildPublicApiUrl('/auth/login')}?returnTo=${encodeURIComponent(target)}${prompt}`
}

/** 当前页面的站内路径（含查询串，不含 hash），作为登录 / 切换账号后的回跳地址。 */
export function currentReturnPath(): string {
  try {
    return (window.location.pathname || '/') + (window.location.search || '')
  } catch {
    return MyAppsPath
  }
}

function toInstalledApp(item: unknown): InstalledApp | null {
  if (!item || typeof item !== 'object') return null
  const raw = item as Record<string, unknown>
  const appName = asText(raw.appName, 80)
  if (!appName) return null
  return {
    appName,
    icon: safeImageUrl(raw.icon),
    versionName: asText(raw.versionName, 40),
    installTime: asTimestamp(raw.installTime),
    updateTime: asTimestamp(raw.updateTime),
  }
}

function asCount(value: unknown, fallback: number): number {
  return typeof value === 'number' && isFinite(value) && value >= 0 ? Math.floor(value) : fallback
}

/** 读 60s 内缓存的「我的应用」第一页；uid 或每页条数与当前不一致即视为没有缓存。 */
export function readCachedFirstPage(uid: string, limit: number = InstalledAppsPageSize): Extract<InstalledAppsResult, { kind: 'ok' }> | null {
  const data = readCache(AppsCacheKey) as { uid?: unknown; limit?: unknown; list?: unknown; total?: unknown } | null
  if (!data || typeof data !== 'object' || data.uid !== uid || data.limit !== limit || !Array.isArray(data.list)) return null
  const list: InstalledApp[] = []
  for (const item of data.list) {
    const app = toInstalledApp(item)
    if (app) list.push(app)
  }
  return { kind: 'ok', list, total: asCount(data.total, list.length), page: 1, pageSize: limit }
}

/** 记下「我的应用」第一页（带 uid，换账号即作废）。 */
export function writeCachedFirstPage(uid: string, result: Extract<InstalledAppsResult, { kind: 'ok' }>, limit: number = InstalledAppsPageSize): void {
  writeCache(AppsCacheKey, { uid, limit, list: result.list, total: result.total })
}

/** 拉取一页「我的应用」。uid 只由后端从会话里取，前端不传（§3 D5）。 */
export async function fetchInstalledApps(page: number, limit: number = InstalledAppsPageSize): Promise<InstalledAppsResult> {
  const query = `?page=${encodeURIComponent(String(page))}&limit=${encodeURIComponent(String(limit))}`
  const result = await requestJsonWithRetry<{ list?: unknown; total?: unknown; page?: unknown; pageSize?: unknown }>(`/me/apps${query}`)
  if (!result) return { kind: 'error' }
  if (result.status === 401) return { kind: 'unauthorized' }
  const body = result.body
  if (result.status !== 200 || !body || body.code !== 200 || !body.data || typeof body.data !== 'object') {
    return { kind: 'error' }
  }
  const data = body.data
  const rawList = Array.isArray(data.list) ? data.list : []
  const list: InstalledApp[] = []
  for (const item of rawList) {
    const app = toInstalledApp(item)
    if (app) list.push(app)
  }
  return {
    kind: 'ok',
    list,
    total: asCount(data.total, list.length),
    page: asCount(data.page, page) || page,
    pageSize: asCount(data.pageSize, limit) || limit,
  }
}

/**
 * 只允许跳到 http(s) 绝对地址或站内路径。endSessionUrl 来自自家后端，这里仍做一次协议白名单，
 * 防止后端配置错误时把 javascript: 之类的地址交给 location。
 */
export function safeNavigationUrl(value: unknown): string {
  const url = asText(value, 4096)
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  if (url.charAt(0) === '/' && url.charAt(1) !== '/' && url.charAt(1) !== '\\') return url
  return ''
}

export type LogoutResult = 'redirected' | 'already-signed-out' | 'failed'

/**
 * 退出登录：POST /api/v1/auth/logout（带 X-Requested-With，配合后端 Origin 校验防 CSRF，§6），
 * 成功后整页跳转到 SSO 的 end_session 地址，由 SSO 删浏览器会话后回到官网首页。
 */
export async function logout(navigate: (url: string) => void = url => window.location.assign(url)): Promise<LogoutResult> {
  const result = await requestJson<{ endSessionUrl?: unknown }>('/auth/logout', {
    method: 'POST',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })
  if (!result) return 'failed'
  if (result.status === 401) {
    // 会话早已失效：本地按未登录处理即可
    markSignedOut()
    return 'already-signed-out'
  }
  const body = result.body
  const okEnvelope = !!body && (body.code === undefined || body.code === 200)
  if (result.status !== 200 || !okEnvelope) return 'failed'
  // 官网会话此刻已在后端删除；拿不到 SSO 登出地址时退回首页，至少保证官网侧已退出
  const target = safeNavigationUrl(body && body.data ? body.data.endSessionUrl : '') || '/'
  markSignedOut()
  navigate(target)
  return 'redirected'
}

/**
 * 切换账号：先走现有退出流程删掉官网会话，再整页跳到登录入口（prompt=login 强制重新输入凭据），
 * 登录完成后回到 returnTo。BFF 的登出接口不支持自定义回跳（SSO 登出只回登记好的官网首页），
 * 所以这里不跳 endSessionUrl，改为直接进登录流程。会话早已失效时同样直接进登录流程。
 */
export async function switchAccount(
  returnTo: string = currentReturnPath(),
  navigate: (url: string) => void = url => window.location.assign(url),
): Promise<LogoutResult> {
  const loginUrl = buildLoginUrl(returnTo, { prompt: 'login' })
  const result = await logout(() => navigate(loginUrl))
  if (result === 'already-signed-out') {
    navigate(loginUrl)
    return 'redirected'
  }
  return result
}

/* ── 登录回调失败提示（/?login_error=<code>）──────────────────────────────── */

/** 回调失败码 → 友好文案。未列出的码统一走默认文案，不把原始错误甩给用户。 */
const LoginErrorMessages: Record<string, string> = {
  access_denied: '你已取消登录。需要时可以随时重新登录。',
  login_required: '登录状态已失效，请重新登录。',
  consent_required: '登录未完成授权，请重新登录。',
  interaction_required: '登录需要你再确认一次，请重新登录。',
  invalid_state: '登录请求已过期或被打断，请重新登录。',
  state_mismatch: '登录请求已过期或被打断，请重新登录。',
  expired: '登录请求已过期，请重新登录。',
  invalid_request: '登录请求无效，请重新登录。',
  invalid_grant: '登录凭证已失效，请重新登录。',
  server_error: '登录服务暂时出了点问题，请稍后再试。',
  temporarily_unavailable: '登录服务繁忙，请稍后再试。',
}

const DefaultLoginErrorMessage = '登录没有完成，请重新登录。如果多次失败，请稍后再试。'

/** 只保留形如错误码的字符，长度封顶，避免把任意 URL 内容原样展示出来。 */
export function normalizeLoginError(value: string | null): string {
  if (!value) return ''
  const code = value.trim().toLowerCase().replace(/[^a-z0-9_.-]/g, '').slice(0, 48)
  return code || 'unknown'
}

export function describeLoginError(code: string): string {
  return LoginErrorMessages[code] || DefaultLoginErrorMessage
}

/**
 * 页面启动时读取的登录失败码。必须在 OfficialApp 清理 URL（sanitizeOfficialPromotionUrl 只保留 code）
 * 之前读到，所以由 main.ts 在挂载前调用一次。
 */
export const loginError = ref('')

export function captureLoginError(): void {
  try {
    const params = new URLSearchParams(window.location.search)
    if (!params.has('login_error')) return
    loginError.value = normalizeLoginError(params.get('login_error'))
    // 读到即从地址栏移除，刷新页面不再重复提示；其余参数原样保留（邀请码等由各页自己处理）
    params.delete('login_error')
    const search = params.toString()
    const url = window.location.pathname + (search ? `?${search}` : '') + window.location.hash
    window.history.replaceState(window.history.state, '', url)
  } catch {
    // 老内核缺 URLSearchParams 时不提示，不影响页面
  }
}

/* ── 时间格式化（北京时间，与首屏下载信息口径一致）──────────────────────── */

const ShanghaiOffsetMs = 8 * 60 * 60 * 1000

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function shanghaiParts(unixSeconds: number) {
  const d = new Date(unixSeconds * 1000 + ShanghaiOffsetMs)
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate(), hh: d.getUTCHours(), mm: d.getUTCMinutes() }
}

/** 北京时间的「第几天」序号，用来判断「昨天」等自然日差。 */
function shanghaiDayIndex(unixSeconds: number): number {
  return Math.floor((unixSeconds * 1000 + ShanghaiOffsetMs) / 86400000)
}

export interface FormattedTime {
  /** 卡片上显示的短文案：刚刚 / 5 分钟前 / 3 小时前 / 昨天 14:20 / 3 天前 / 2026-10-01 */
  text: string
  /** 完整的北京时间，用于 title 与读屏：2026-10-28 14:20 */
  full: string
  /** <time datetime> 用的 ISO 字符串 */
  iso: string
}

/** 秒级时间戳 → 相对 / 绝对时间。7 天内用相对说法，更早显示日期；不依赖浏览器时区与 Intl。 */
export function formatAppTime(unixSeconds: number, nowMs: number = Date.now()): FormattedTime | null {
  if (!unixSeconds) return null
  const p = shanghaiParts(unixSeconds)
  const date = `${p.y}-${pad2(p.m)}-${pad2(p.d)}`
  const full = `${date} ${pad2(p.hh)}:${pad2(p.mm)}`
  const iso = new Date(unixSeconds * 1000).toISOString()
  const diff = Math.floor(nowMs / 1000) - unixSeconds
  let text = date
  if (diff < -300) {
    text = date // 时间在未来（设备时钟偏差）：直接给日期，不说「刚刚」
  } else if (diff < 60) {
    text = '刚刚'
  } else if (diff < 3600) {
    text = `${Math.floor(diff / 60)} 分钟前`
  } else {
    const dayDiff = shanghaiDayIndex(Math.floor(nowMs / 1000)) - shanghaiDayIndex(unixSeconds)
    if (dayDiff <= 0) text = `${Math.floor(diff / 3600)} 小时前`
    else if (dayDiff === 1) text = `昨天 ${pad2(p.hh)}:${pad2(p.mm)}`
    else if (dayDiff < 7) text = `${dayDiff} 天前`
  }
  return { text, full, iso }
}
